import { Button, Form, App, Input, Modal } from 'antd'
import { confirmSignIn } from 'aws-amplify/auth/cognito'
import React, { useCallback, useState } from 'react'

import { useAppDispatch } from '@/redux/utils'
import { init } from '@/utils/init'
import { required, sameAs } from '@/utils/rules'

const { useForm } = Form

export interface FormValues {
  password: string;
  confirmPassword: string;
}

export interface ConfirmPasswordModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

const passwordRules = [required()]
const confirmPasswordRules = [required(), sameAs('password')]

export default function ConfirmPasswordModal({
  open,
  setOpen,
}: ConfirmPasswordModalProps) {
  const [form] = useForm<FormValues>()
  const { message } = App.useApp()
  const dispatch = useAppDispatch()
  const [submitting, setSubmitting] = useState(false)

  const onFinish = useCallback(
    async ({ password }: FormValues) => {
      setSubmitting(true)
      try {
        const { isSignedIn, nextStep } = await confirmSignIn({
          challengeResponse: password,
        })
        if (isSignedIn && nextStep.signInStep === 'DONE') {
          await init(dispatch)
          setOpen(false)
        } else {
          void message.error('Setup new password failed')
        }
      } catch (error) {
        if (error instanceof Error) {
          void message.error(error.message)
        }
      }
      setSubmitting(false)
    },
    [message, setOpen, dispatch],
  )

  return (
    <Modal
      title="New Password Required"
      centered
      footer={null}
      open={open}
      onCancel={() => setOpen(false)}
    >
      <Form<FormValues> layout="vertical" form={form} onFinish={onFinish}>
        <Form.Item label="Password" name="password" rules={passwordRules}>
          <Input.Password placeholder="Password" />
        </Form.Item>

        <Form.Item
          label="Confirm Password"
          name="confirmPassword"
          dependencies={['password']}
          rules={confirmPasswordRules}
        >
          <Input.Password placeholder="Confirm Password" />
        </Form.Item>
        <Form.Item shouldUpdate>
          {() => (
            <Button
              size="middle"
              type="primary"
              htmlType="submit"
              loading={submitting}
            >
              Submit
            </Button>
          )}
        </Form.Item>
      </Form>
    </Modal>
  )
}
